import {
  getCommitSha,
  getEnv,
  isLocalDevelopmentEnvironment,
  isProductionEnvironment,
  isTestEnvironment,
} from "@remi/environment";
import { BaseError } from "@remi/error-core/BaseError"; // oxlint-disable-line no-restricted-imports -- existing import; the call site was never migrated
import { reportToSentry } from "@remi/sentry";

import { isTransientAxiomIngestError } from "./axiom-transient-error";
import { emptyLogger, RemiLogger } from "./remi-logger";
import type { ILogger } from "./types";

interface DeployLogContext {
  commitSha?: string;
  environment?: string;
  deploymentId?: string;
}

/**
 * Lets Argos release watch tie each line to the deploy that wrote it. Only `logs` already has these columns, and a
 * top-level field new to a dataset gets its whole batch rejected at ingest (`logs-preview`, `logs-scripts`).
 */
export function getDeployLogContext(): DeployLogContext {
  const commitSha = getCommitSha();
  if (commitSha === null || !isProductionEnvironment() || getEnv("AXIOM_DATASET") !== "logs") {
    return {};
  }
  // Winston copies defaultMeta over the keys of a line and of a child logger, so an undefined key would erase theirs.
  const deploymentId = getEnv("VERCEL_DEPLOYMENT_ID");
  return { commitSha, environment: "production", ...(deploymentId === undefined ? {} : { deploymentId }) };
}

let logger: ILogger = emptyLogger;

let flushAxiomLogs: () => Promise<void> = async () => {};

if (typeof window === "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { createLogger: createWinstonLogger, format, transports } = require("winston");
  const { combine, colorize, json, simple, prettyPrint } = format;
  const defaultMeta = { service: getEnv("SERVICE_NAME"), ...getDeployLogContext() };

  const consoleTransport = new transports.Console();

  // CI Logger Settings
  if (isTestEnvironment()) {
    logger = createWinstonLogger({
      level: "info",
      format: simple(),
      defaultMeta,
      transports: [consoleTransport],
    });
  }
  // Development Logger Settings
  else if (isLocalDevelopmentEnvironment()) {
    const devConsoleTransport = new transports.Console({
      format: combine(colorize(), simple(), prettyPrint()),
    });

    const filename = getDevLogFilePath();

    const fileTransport = filename ? new transports.File({ filename, format: json() }) : null;

    const devTransports = [devConsoleTransport];
    if (fileTransport) {
      devTransports.push(fileTransport);
    }

    logger = createWinstonLogger({ level: "info", defaultMeta, transports: devTransports });
  }
  // Production Logger Settings
  else {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { WinstonTransport: AxiomTransport } = require("@axiomhq/winston");

    const AXIOM_TRANSPORT_ERROR_LOG_WINDOW_MS = 60_000;
    let lastAxiomTransportErrorLogAt = 0;

    // `onError` is forwarded to the underlying @axiomhq/js client, whose default is
    // `console.error`. Without overriding it, ingest timeouts (DOMException
    // TIMEOUT_ERR from AbortSignal.timeout) surface as error-level Vercel logs on
    // every request. The Axiom client swallows ingest errors into a resolved
    // promise, so Winston's `error` event is never emitted for this path —
    // `onError` is the only intercept.
    const handleAxiomTransportError = (err: unknown) => {
      const now = Date.now();
      if (now - lastAxiomTransportErrorLogAt < AXIOM_TRANSPORT_ERROR_LOG_WINDOW_MS) {
        return;
      }
      lastAxiomTransportErrorLogAt = now;
      const error = err instanceof Error ? err : undefined;
      const errorName = error?.name ?? typeof err;
      const errorMessage = error?.message ?? String(err);
      console.warn("Axiom transport failed.", { errorMessage });

      // Rate limits and timeouts clear on their own, so Sentry would only flood.
      if (isTransientAxiomIngestError(err)) {
        return;
      }
      // A rejected ingest discards the entire batch, so the dropped lines cannot report their own
      // loss and stdout is the only other sink — Sentry is the sole channel that can raise it.
      reportToSentry(new BaseError({ message: "Axiom log ingest was rejected", cause: error }), {
        Axiom: { errorName, errorMessage },
      });
    };

    const axiomTransport = new AxiomTransport({
      dataset: getEnv("AXIOM_DATASET"),
      token: getEnv("AXIOM_TOKEN"),
      onError: handleAxiomTransportError,
    });

    flushAxiomLogs = () =>
      new Promise<void>((resolve) => {
        // Flushing an empty batch posts an empty body, which Axiom rejects with
        // "payload in body is required". Callers flush after every request, so a request that
        // logged nothing would otherwise POST — and error — on every invocation.
        if (axiomTransport.batch.length === 0) {
          resolve();
          return;
        }
        axiomTransport.flush(() => resolve());
      });

    const winstonLogger = createWinstonLogger({
      level: "info",
      format: json(),
      defaultMeta,
      transports: [axiomTransport, consoleTransport],
    });

    winstonLogger.on("error", (err: unknown) => {
      console.error("Winston logger error", err);
    });

    logger = winstonLogger;
  }
}

export const remiLogger = new RemiLogger(logger);

export const flushLogs = (): Promise<void> => flushAxiomLogs();

function getDevLogFilePath(): string | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const fs = require("node:fs");
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const path = require("node:path");

    let dir = process.cwd();
    while (dir !== path.dirname(dir)) {
      if (fs.existsSync(path.join(dir, "pnpm-workspace.yaml"))) {
        break;
      }
      dir = path.dirname(dir);
    }

    const devLogDir = path.join(dir, ".dev-logs");
    if (!fs.existsSync(devLogDir)) {
      fs.mkdirSync(devLogDir, { recursive: true });
    }

    return path.join(devLogDir, "logs.jsonl");
  } catch (error) {
    console.error("Error getting dev log file path", error);
    return null;
  }
}
